import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import type { ClientProject } from '@/types';

const STATUS_COLORS: Record<string, string> = {
  setup: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
  configured: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
  handoff_pending: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200',
  active: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
  archived: 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200',
};

const STATUS_LABELS: Record<string, string> = {
  setup: 'Setup',
  configured: 'Configured',
  handoff_pending: 'Handoff Pending',
  active: 'Active',
  archived: 'Archived',
};

interface ClientProjectCardProps {
  clientProject: ClientProject;
}

const ClientProjectCard: React.FC<ClientProjectCardProps> = ({ clientProject }) => {
  const navigate = useNavigate();

  const address = [clientProject.address?.city, clientProject.address?.state]
    .filter(Boolean)
    .join(', ');

  return (
    <Card className="hover:shadow-md transition-shadow">
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between">
          <CardTitle className="text-base">{clientProject.name}</CardTitle>
          <Badge className={STATUS_COLORS[clientProject.status] || STATUS_COLORS.setup}>
            {STATUS_LABELS[clientProject.status] || clientProject.status}
          </Badge>
        </div>
        {address && (
          <p className="text-sm text-muted-foreground">{address}</p>
        )}
      </CardHeader>
      <CardContent>
        <div className="flex items-center justify-between">
          <div className="text-sm text-muted-foreground">
            <span className="capitalize">{clientProject.projectType}</span>
            {clientProject.linkedProjects?.length > 0 && (
              <span className="ml-2">
                &middot; {clientProject.linkedProjects.length} project{clientProject.linkedProjects.length !== 1 ? 's' : ''}
              </span>
            )}
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate(`/service-provider/projects/${clientProject._id}`)}
            >
              Details
            </Button>
            {clientProject.linkedProjects?.[0]?.projectId && (
              <Button
                size="sm"
                onClick={() => navigate(`/editor/${clientProject.linkedProjects[0].projectId}`)}
              >
                Configure
              </Button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default ClientProjectCard;
